import React, { useEffect, useState, useMemo } from 'react';
import Axios from 'axios';

const Reports = () => {
  const [individualData, setIndividualData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    
    Promise.all([
      Axios.get(`http://localhost:3001/api/reports/individual/${userId}`),
      Axios.get(`http://localhost:3001/api/reports/monthly/${userId}`)
    ])
      .then(([resIndiv, resMonthly]) => {
        setIndividualData(resIndiv.data || []);
        setMonthlyData(resMonthly.data || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching reports:", err);
        setLoading(false);
      });
  }, []);

  // --- CALCULATIONS FOR TOP METRICS ---
  const totalInvoices = individualData.length;
  const totalDueSum = individualData.reduce((acc, row) => acc + (Number(row.duebal) || 0), 0);
  const totalDiscountSum = individualData.reduce((acc, row) => acc + (Number(row.totalDiscounts) || 0), 0);
  
  const totalVatSum = individualData.reduce((acc, row) => {
    const vatPercent = typeof row.vat === 'string' 
      ? Number(row.vat.replace(/[^0-9.]/g, '')) 
      : (Number(row.vat) || 0);
    return acc + ((Number(row.netRevenue || 0) - Number(row.duebal || 0)) * (vatPercent / 100));
  }, 0);
  
  const totalNetRevenueIncludingVat = individualData.reduce((acc, curr) => acc + Number(curr.netRevenue || 0), 0);
  const netRevenueExcludingVat = (totalNetRevenueIncludingVat - totalVatSum) - totalDueSum;

  const totalFiscalYears = useMemo(() => {
      if (individualData.length === 0) return 0;
      const dates = individualData.map(row => new Date(row.month).getTime()).filter(t => !isNaN(t));
      if (dates.length === 0) return 1;
      const minDate = new Date(Math.min(...dates));
      const maxDate = new Date(Math.max(...dates));

      let years = maxDate.getFullYear() - minDate.getFullYear();
      if (maxDate.getMonth() < minDate.getMonth() || 
         (maxDate.getMonth() === minDate.getMonth() && maxDate.getDate() < minDate.getDate())) {
        years--;
      }
      return Math.max(1, years + 1); 
    }, [individualData]);

  // Helper map to convert any text month string to its index number
  const monthNameToIndex = useMemo(() => ({
    january: 0, jan: 0, "01": 0,
    february: 1, feb: 1, "02": 1,
    march: 2, mar: 2, "03": 2,
    april: 3, apr: 3, "04": 3,
    may: 4, "05": 4,
    june: 5, jun: 5, "06": 5,
    july: 6, jul: 6, "07": 6,
    august: 7, aug: 7, "08": 7,
    september: 8, sep: 8, sept: 8, "09": 8,
    october: 9, oct: 9, "10": 9,
    november: 10, nov: 10, "11": 10,
    december: 11, dec: 11, "12": 11
  }), []);

  if (loading) return <div style={{ color: 'white', padding: '20px' }}>Loading Financial Data...</div>;

  return (
    <div style={containerStyle}>
      <h2 style={{ color: '#e9edef', marginBottom: '20px', borderBottom: '2px solid #00a884', paddingBottom: '10px' }}>
        Financial Overview Monthly Report
      </h2>

      {/* TOP METRICS CARDS */}
      <div style={statsGrid}>
        <div style={cardStyle}>
          <span style={labelStyle}>Total Invoices</span>
          <h2 style={valueStyle}>{totalInvoices}</h2>
        </div>
        <div style={cardStyle}>
          <span style={labelStyle}>Total Age of Business</span>
          <h2 style={valueStyle}>{totalFiscalYears} Year(s)</h2>
        </div>
        <div style={cardStyle}>
          <span style={labelStyle}>Total Discount Given</span>
          <h2 style={{ ...valueStyle, color: '#e74c3c' }}>TK {totalDiscountSum.toLocaleString()}/-</h2>
        </div>
        <div style={cardStyle}>
          <span style={labelStyle}>Total VAT Collected</span>
          <h2 style={{ ...valueStyle, color: '#ffd000' }}>TK {Math.round(totalVatSum).toLocaleString()}/-</h2>
        </div>
        <div style={cardStyle}>
          <span style={labelStyle}>Total Due Balance</span>
          <h2 style={{ ...valueStyle, color: '#ff1900' }}>TK {totalDueSum.toLocaleString()}/-</h2>
        </div>
        <div style={cardStyle}>
          <span style={labelStyle}>Total Net Revenue (Excl. VAT)</span>
          <h2 style={{ ...valueStyle, color: '#00a884' }}>TK {Math.round(netRevenueExcludingVat).toLocaleString()}/-</h2>
        </div>
      </div>

      {/* MONTHLY BREAKDOWN TABLE */}
      <div style={tableWrapper}>
        <h3 style={{ padding: '15px', margin: 0, backgroundColor: '#011433' }}>Monthly Breakdown</h3>
        <table style={tableStyle}>
          <thead>
            <tr style={headerRowStyle}>
              <th>SL No.</th>
              <th>Month</th>
              <th>Invoices</th>
              <th>Gross Revenue</th>
              <th>Discount Amount</th>
              <th>Taxable Amount</th>
              <th>Value Added Tax<br/>(VAT)</th>
              <th>Revenue (Incl. VAT)</th>
              <th>Amount Received</th>
              <th>Due Balance</th>
              <th>Net Revenue (Excl. VAT)</th>
            </tr>
          </thead>
          <tbody>
          {monthlyData.map((row, index) => {
            const gross = Number(row.mgrossRevenue) || 0;
            const discount = Number(row.mtotalDiscounts) || 0;
            const totdue = Number(row.mduebal) || 0;
            const totnetrev = Number(row.mnetRevenue) || 0;
            
            const taxableAmount = (gross - discount);
            const NetReceivedAmount = totnetrev - totdue;

            // 1. RESOLVE MONTH INDEX AND YEAR FROM THE CURRENT TABLE ROW
            const targetMonthStr = String(row.mmonth || row.month || "").toLowerCase().trim();
            let targetMonthIndex = monthNameToIndex[targetMonthStr];
            let targetYear = Number(row.myear || row.year || 0);
            
            // If the row month field is a parseable date string (e.g., "2025-01" or "January 2025")
            if (!isNaN(Date.parse(targetMonthStr))) {
              const rowDateObj = new Date(targetMonthStr);
              targetMonthIndex = rowDateObj.getMonth();
              if (!targetYear) {
                targetYear = rowDateObj.getFullYear();
              }
            }

            // 2. FILTER INVOICES: Match BOTH Month Index AND Year
            const itemsForThisMonth = individualData.filter(item => {
              if (!item.month) return false;
              const itemDate = new Date(item.month);
              if (isNaN(itemDate.getTime())) return false;
              
              const matchesMonth = itemDate.getMonth() === targetMonthIndex;
              
              // If targetYear was found, enforce year matching; otherwise, match by month only
              const matchesYear = targetYear ? itemDate.getFullYear() === targetYear : true;
              
              return matchesMonth && matchesYear;
            });

            // 3. SUM UP THE CALCULATED VAT FOR ALL MATCHED INVOICES
            const tRevenuevat = itemsForThisMonth.reduce((acc, item) => {
              const vatPercent = typeof item.vat === 'string' 
                ? Number(item.vat.replace(/[^0-9.]/g, '')) 
                : (Number(item.vat) || 0);
              const itemReceived = (Number(item.netRevenue) || 0) - (Number(item.duebal) || 0);
              return acc + (itemReceived * (vatPercent / 100));
            }, 0);
            
            // 4. Calculate Net Profit Excl. VAT
            const NetProfitAmount = NetReceivedAmount - tRevenuevat;
            
            // Format the displayed month column to include the year if available
            const displayMonth = targetYear && !targetMonthStr.includes(String(targetYear))
              ? `${row.mmonth} ${targetYear}`
              : row.mmonth;
              
              return (
                <tr key={index} style={rowStyle}>
                  <td>{index + 1}</td>
                  <td>{displayMonth}</td>
                  <td>{row.minvoiceCount}</td>
                  <td>TK {gross.toLocaleString()}</td>
                  <td style={{ color: '#e74c3c' }}>- TK {discount.toLocaleString()}</td>
                  <td>TK {taxableAmount.toLocaleString()}</td>
                  <td style={{ color: '#ffd000' }}>+ TK {Math.round(tRevenuevat).toLocaleString()}</td>
                  <td style={{ color: '#fff' }}>TK {totnetrev.toLocaleString()}</td>
                  <td style={{ fontWeight: 'bold', color: '#00a7fa' }}>TK {NetReceivedAmount.toLocaleString()}</td>
                  <td style={{ fontWeight: 'bold',  color: totdue === 0 ? '#ffd001' : '#ff3300' }}>{totdue === 0 ? "---" : `TK ${totdue.toLocaleString()}`}</td> 
                  <td style={{ fontWeight: 'bold', color: '#00a884' }}>TK {Math.round(NetProfitAmount).toLocaleString()}</td> 
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// --- STYLES ---
const containerStyle = { padding: '10px', maxWidth: '1920px', margin: '0 auto' };
const statsGrid = { display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '15px', marginBottom: '30px' }; 
const cardStyle = { backgroundColor: '#011433', padding: '15px', borderRadius: '12px', textAlign: 'center', boxShadow: '0 4px 6px rgba(0,0,0,0.3)' };
const labelStyle = { color: '#ffffff', fontSize: '12px', textTransform: 'uppercase' }; 
const valueStyle = { fontSize: '20px', margin: '10px 0 0 0', color: '#ffffff' };
const tableWrapper = { backgroundColor: '#011433', borderRadius: '12px', overflow: 'hidden', color: '#e9edef' };
const tableStyle = { width: '100%', borderCollapse: 'collapse' };
const headerRowStyle = { backgroundColor: '#000b1d', textAlign: 'left', color: '#8696a0' };
const rowStyle = { borderBottom: '1px solid #202c33', padding: '15px' };

export default Reports;
